import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Reveal, Overline } from "@/components/motion";

const EMPTY = { company: "", location: "", headcount: "21–50", venue_type: "Workplace / office", charity_ideas: "", contact_name: "", email: "", phone: "" };
const inputCls = "w-full rounded-xl border border-[#E2DDD3] bg-white px-4 py-3 text-sm text-[#0B1E17] placeholder:text-[#52665E]/50 focus:outline-none focus:ring-2 focus:ring-[#1B5E46]/40 focus:border-[#1B5E46] transition";

export const EnquiryForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", form);
      setDone(true);
      toast.success("Enquiry received! We'll reach out within 24 hours to schedule a free office tasting.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="enquire" data-testid="enquiry-section" className="py-24 sm:py-32 bg-[#0F2E23] text-[#FAF9F6] relative grain overflow-hidden">
      <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#34D399]/10 blur-[120px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
        <div>
          <Reveal><Overline dark>Host a machine</Overline></Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-4 font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl leading-tight">
              Bring Give &amp; Grind to your space.
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 text-base sm:text-lg text-[#FAF9F6]/65 leading-relaxed max-w-md">
              Workplace, gym, student halls, leisure centre — if people gather there, it can fund good. Tell us about your venue and the causes your people care about. We'll come back within one working day.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <ul className="mt-8 space-y-3 text-sm text-[#FAF9F6]/70">
              {["Zero install or rental cost", "We stock, clean and service everything", "You choose the charity — we handle the rest"].map((t) => (
                <li key={t} className="flex items-center gap-3">
                  <span className="w-5 h-5 rounded-full bg-[#D97706] text-white flex items-center justify-center text-[10px] font-bold">✓</span>
                  {t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          {done ? (
            <div data-testid="enquiry-success" className="h-full min-h-[320px] bg-[#13271F] border border-[#34D399]/30 rounded-3xl p-10 flex flex-col justify-center items-start gap-4">
              <p className="font-display font-extrabold text-3xl text-[#34D399]">Thank you.</p>
              <p className="text-[#FAF9F6]/70 text-sm leading-relaxed max-w-sm">
                Your enquiry is in. The Give &amp; Grind team will be in touch within one working day to talk machines, charities and a free office tasting.
              </p>
              <button data-testid="enquiry-again-btn" onClick={() => { setDone(false); setForm(EMPTY); }}
                className="text-sm font-bold text-[#D97706] hover:text-[#F59E0B] transition-colors">
                Submit another enquiry →
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="host-enquiry-form" className="bg-[#F7F5F0] rounded-3xl p-7 sm:p-9 space-y-4 text-[#0B1E17]">
              <div className="grid sm:grid-cols-2 gap-4">
                <input required data-testid="enquiry-company" placeholder="Company name*" value={form.company} onChange={set("company")} className={inputCls} />
                <input required data-testid="enquiry-location" placeholder="Venue location / city*" value={form.location} onChange={set("location")} className={inputCls} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <select data-testid="enquiry-venue-type" value={form.venue_type} onChange={set("venue_type")} className={inputCls}>
                  {["Workplace / office", "Gym or fitness studio", "Student halls", "Leisure centre", "Other venue"].map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
                <select data-testid="enquiry-headcount" value={form.headcount} onChange={set("headcount")} className={inputCls}>
                  {["1–20", "21–50", "51–100", "101–250", "250+"].map((h) => <option key={h} value={h}>{h} people</option>)}
                </select>
              </div>
              <input data-testid="enquiry-charity" placeholder="Charity ideas (optional)" value={form.charity_ideas} onChange={set("charity_ideas")} className={inputCls} />
              <div className="grid sm:grid-cols-2 gap-4">
                <input required data-testid="enquiry-contact-name" placeholder="Your name*" value={form.contact_name} onChange={set("contact_name")} className={inputCls} />
                <input required type="email" data-testid="enquiry-email" placeholder="Work email*" value={form.email} onChange={set("email")} className={inputCls} />
              </div>
              <input data-testid="enquiry-phone" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className={inputCls} />
              <button type="submit" disabled={busy} data-testid="enquiry-submit-btn"
                className="w-full rounded-full bg-[#D97706] text-white font-bold py-4 text-sm hover:bg-[#F59E0B] disabled:opacity-60 transition-colors duration-300">
                {busy ? "Sending…" : "Submit host enquiry"}
              </button>
              <p className="text-xs text-[#52665E]/70 text-center">No spam, no obligation. We reply within one working day.</p>
              <p className="text-xs text-[#1B5E46] text-center font-semibold">Full service across London &amp; Home Counties (to Oxford &amp; Reading) — supply-and-support anywhere else in the UK.</p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
